import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import {
  BillingProfileFormField,
  BillingProfileSelectItem,
  commonContexts,
  EntitlementIcon,
  entitlementUtils,
  ErrorComponents,
  InflightInvoiceBanner,
  InfoPanel,
  InvoiceGroupSelectItem,
  isAccess,
  NotificationTypes,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import {
  currentEntitlementType,
  getLinkedEntitlementsDetails,
  getParentContainerId,
} from '@css-xp/base/entitlement-utils';
import { InvoiceGroup, Maybe } from '@css-xp/graphtypes';
import {
  EntitlementDetails,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { INVOICE_GROUP_PATH_2, Permissions, UpdateOrderRequest } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import WarningDialog from '../../warning-dialog';
import './entitlement-set-invoice-group-pane.scss';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import styled from '@emotion/styled';

const InlineDialog = styled(TooltipPrimitive)`
  background: white;
  max-height: 120px;
  max-width: 328px;
  padding: 20px 16px;
  box-shadow: 0px 8px 12px rgba(9, 30, 66, 0.15), 0px 0px 1px rgba(9, 30, 66, 0.31);
`;

interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: commonContexts.TransactionAccountContextType;
}

interface FormData {
  entitlementId?: string;
  invoiceGroupId?: InvoiceGroupSelectItem;
}

const getDefaultBillingGroup = (invoiceGroups: InvoiceGroup[], invoiceGroupId: string) => {
  return invoiceGroups.find((invoiceGroupItem) => {
    return invoiceGroupItem.id === invoiceGroupId;
  });
};

const getInvalidMessage = (billingProfile: Maybe<InvoiceGroup>): React.ReactNode | null => {
  const invalidMessage: React.ReactNode[] = [];
  if (!billingProfile) {
    return null;
  }
  if (!billingProfile.defaultPaymentMethod) {
    invalidMessage.push(
      <div>No payment method is available for the billing profile (the customer will need to add it)</div>
    );
  }
  if (!billingProfile.shipToParty) {
    invalidMessage.push(<div>No sold-to address available for the billing profile</div>);
  }

  return invalidMessage;
};

export const EntitlementSetInvoiceGroupPane = ({ entitlement, transactionAccountContext, refetch }: Props) => {
  const startTime = new Date().getTime();
  const { closeSidebar } = useSidebar();
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = useState(false);
  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
    UpdateOrderRequest,
    any
  >({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });
  const [linkedEntitlementsData, setLinkedEntitlementsData] = useState<Array<LinkedDestinationEntitlement>>([]);
  let selectedBillingProfile: Maybe<InvoiceGroup> = null;
  const [isEnabled] = useFeatureToggle();
  const isEntRelationShipFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const [selectionErrors, setSelectionErrors] = useState<React.ReactNode>(null);
  const [selectedBillingProfileId, setSelectedBillingProfileId] = useState<Maybe<string>>(
    entitlement.activeSubscriptionBillingGroupId ?? null
  );

  useEffect(() => {
    setSelectionErrors(getInvalidMessage(selectedBillingProfile));
  }, [selectedBillingProfile]);

  const linkedEntsTableVisible = useMemo(() => {
    if (isEntRelationShipFeatureEnable) {
      if (isStatuspage(entitlement?.productId)) {
        return true;
      }
      return !!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length;
    }
    return false;
  }, [isEntRelationShipFeatureEnable, entitlement]);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  const constructRequestData = (entitlement: EntitlementDetails, formData: FormData) => {
    const requestData: UpdateOrderRequest = {
      entitlementId: entitlement.id,
      invoiceGroupId: formData?.invoiceGroupId?.value,
      offeringId: entitlement?.offeringId ?? undefined,
      productId: entitlement?.productId ?? '',
    };

    if (isEntRelationShipFeatureEnable) {
      // send the entitlement, which is having active subscriptions
      requestData.linkedEntitlements = linkedEntitlementsData.length
        ? getLinkedEntitlementsDetails(
            entitlement,
            linkedEntitlementsData.filter((linkedEntitlementObj) => linkedEntitlementObj.subscriptionId)
          )
        : [];
    }

    return requestData;
  };

  const handleFormSubmit = (formData: FormData) => {
    if (warningModalDailogVisible) {
      setConfirmDialogOpen(true);
    } else {
      logTimeStamp(startTime, 'Switch billing profile', SignalFxFEPerfStore.FE_USER_TASK_SWITCH_BILLING_PROFILE_TIME);
      const requestData = constructRequestData(entitlement, formData);
      submitForm(requestData);
    }
  };

  const handleBillingProfileChange = (selectedItem: BillingProfileSelectItem) => {
    setSelectedBillingProfileId(selectedItem?.invoiceGroup?.id);
    setSelectionErrors(getInvalidMessage(selectedItem?.invoiceGroup));
  };

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-ig-success')
      .withDismissAfter(5000);
    if (response.transitionTime === 'SCHEDULED') {
      const notification = notificationOptions
        .withHeader('Billing profile switch scheduled')
        .withMessage('Billing profile switch has been scheduled for the next billing cycle')
        .build();
      sendSuccessNotification(notification);
      setNotificationSent(true);
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.CHANGE_SCHEDULED,
        actionSubject: AnalyticsTrackSubject.BILLING_PROFILE,
        attributes: {
          entitlementId: entitlement.id,
        },
      });
    } else {
      const notification = notificationOptions
        .withHeader('Billing profile switched successfully')
        .withMessage(
          <div>
            <p>
              You can view the change in the <strong>order history</strong> tab.
            </p>
            <Link to={`#order-history`}>View order history</Link>
          </div>
        )
        .build();
      sendSuccessNotification(notification);
      setNotificationSent(true);
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.CHANGE_SCHEDULED,
        actionSubject: AnalyticsTrackSubject.BILLING_PROFILE,
        attributes: {
          entitlementId: entitlement.id,
        },
      });
    }
  }

  const { data, loading, error, getTransactionAccount, getCreditDetails } = transactionAccountContext;

  if (loading) return <LoadingIndicator />;

  const transactionAccount = getTransactionAccount(data);
  const creditDetails = getCreditDetails && getCreditDetails(data);

  if (error && !transactionAccount) return <ErrorComponents.GenericErrorPage error={error} />;
  if (error && transactionAccount) {
    // eslint-disable-next-line no-console
    console.error('Error on set-invoice-group-pane transactionAccount', error);
  }

  const isTermPaymentWriteEnabled = isAccess(Permissions.PAYMENT_METHOD_ON_TERM_WRITE);

  const billingProfiles = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as Maybe<InvoiceGroup[]>;

  selectedBillingProfile =
    entitlement?.billingGroupId && billingProfiles && billingProfiles.length
      ? getDefaultBillingGroup(billingProfiles, entitlement?.billingGroupId)
      : null;

  const handleConfirmDialog = (formData: FormData) => {
    setConfirmDialogOpen(false);
    const requestData = constructRequestData(entitlement, formData);
    submitForm(requestData);
  };

  return (
    <div className="entitlement-update-invoice-group-form sidebar-pane">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty, getState }) => (
          <form {...formProps}>
            <FormHeader title="Switch billing profile">
              <div className="sub-header">
                <EntitlementIcon
                  logoId={entitlement?.logoId as string}
                  productId={entitlement?.productId as string}
                  isCloud={true}
                  className="avatar medium"
                />
                <h4 className="sub-header--text">{`${entitlement?.productName ?? 'Unnamed product'} ${
                  entitlement?.entitlementNumber
                }`}</h4>
              </div>
            </FormHeader>
            {linkedEntsTableVisible && (
              <>
                <FormSection>
                  <entitlementUtils.LinkedEntitlementsTable
                    message="This action will also change the billing profile of these subscriptions:"
                    entitlementId={entitlement.id ?? ''}
                    parentContainerID={getParentContainerId(entitlement)}
                    productType={entitlement.productType}
                    currentEntitlementType={currentEntitlementType(entitlement)}
                    destinationEntitlementType={[
                      LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
                      LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
                      LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
                      LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
                    ]}
                    findApps={true}
                    findProducts={true}
                    updateLinkedEntitlements={setLinkedEntitlementsData}
                    setLoading={setLoadingLinkedEntitlements}
                  />
                </FormSection>
              </>
            )}
            <FormSection>
              <InflightInvoiceBanner
                transactionAccountId={entitlement.transactionAccountId as string}
                message={'All changes will be reflected to the orders included in the inflight invoice'}
              />
              {!formError && selectedBillingProfileId && (
                <InfoPanel
                  type="information"
                  message={
                    <>
                      <p>
                        To edit the billing profile information that’s linked to this subscription, visit the billing
                        profile page.
                      </p>
                      <p className="billing-profile-link">
                        <Link to={`${INVOICE_GROUP_PATH_2}/${selectedBillingProfileId}`}>Edit billing profile</Link>
                      </p>
                    </>
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to apply the changes"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!isEmpty(selectionErrors) && (
                <SectionMessage
                  title="Billing profile needs updating first"
                  appearance="warning"
                  actions={[
                    <SectionMessageAction
                      key="billing-info"
                      href={`${INVOICE_GROUP_PATH_2}/${selectedBillingProfileId}`}
                    >
                      Add information to billing profile
                    </SectionMessageAction>,
                  ]}
                >
                  <>
                    <p>The switch can’t be made because there’s:</p>
                    <p>{selectionErrors}</p>
                    <p>
                      Add the information or switch the billing profile, then try again. Still having trouble? Contact
                      the <strong>#help-advocate-central</strong> channel
                    </p>
                  </>
                </SectionMessage>
              )}
            </FormSection>
            <FormSection>
              <BillingProfileFormField
                fieldLabel={'Billing profile'}
                productId={entitlement?.productId ?? undefined}
                showTermPaymentMethodGroup={isTermPaymentWriteEnabled}
                billingProfiles={billingProfiles}
                entitlementBillingInvoiceGroupId={entitlement?.billingGroupId}
                selected={selectedBillingProfile}
                onChange={handleBillingProfileChange}
                className="billing-profile-form-field"
                billingFrequency={entitlement?.billingFrequency}
                endTimeStamp={entitlement?.endTimestamp as number}
                creditDetails={creditDetails}
              />
              <div className="billing-profile-form-field-info">
                <Tooltip
                  component={InlineDialog}
                  content=" Not all billing profiles are supported for this subscription. Change the plan details to use an
                  existing billing profile or create a new one with a different payment method."
                >
                  <InfoIcon size="medium" label="" primaryColor="#6554C0" />
                </Tooltip>
                <p>Can't find the billing profile you're looking for?</p>
              </div>
            </FormSection>

            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={isProcessing}
                  isDisabled={!dirty || !isEmpty(selectionErrors) || loadingLinkedEntitlement}
                  testId="submit-billing-profile"
                >
                  Confirm
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
            {warningModalDailogVisible && (
              <WarningDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() => handleConfirmDialog(getState().values)}
                header={'Confirm switch'}
                description={
                  <p>
                    This will also change the billing profile for other product and app subscriptions on this site that
                    belong to the same product family.
                  </p>
                }
                okLabel={'Confirm'}
                testId={'IGSubmission'}
              />
            )}
          </form>
        )}
      </Form>
    </div>
  );
};
